/**
 * Page-body translations (English + Simplified Chinese), mirroring the hub's
 * copy.ts pattern: a `DICT` keyed by language, `type Dict = typeof DICT.en`,
 * and a `useT()` hook that returns the dictionary for the shared language
 * (read from `lab:lang` via useLang in prefs.tsx).
 *
 * Function values are annotated `: string` on purpose — a bare arrow returning a
 * template literal infers a narrow literal-union type, which then fails to match
 * between `en` and `zh-CN` and breaks `Dict`.
 *
 * Brand and product names stay in English in both languages: SandBase, Pelican
 * Test, Endless Runner, Watermelon Test, model names, and the tech tokens
 * LLM / SVG / Three.js / HTML5.
 */
import { modelLabel } from './format'
import { SCENES, type SceneSlug } from './scenes'
import { useLang } from './prefs'
import type { LabResult } from './types'

/** Brand name without a leading article, for running copy ("Run <brand>"). */
const brand = (scene: SceneSlug): string => SCENES[scene].name.replace(/^the\s+/i, '')
/** English running-copy form: "the Pelican Test" (mirrors scenes.ts theSceneName). */
const theEn = (scene: SceneSlug): string => `the ${brand(scene)}`

type ShareInput = Pick<LabResult, 'model' | 'score' | 'ok' | 'classic' | 'title'>

const en = {
  header: {
    myRuns: 'My runs',
    signIn: 'Sign in',
    start: 'Start building',
    console: 'Console',
    menu: 'Menu',
    close: 'Close',
    switchScene: 'Switch scene',
    scene: 'SCENE',
    primaryNav: 'Primary navigation',
    mobileNav: 'Mobile navigation',
    closeMenu: 'Close menu',
    home: 'SandBase home',
    announceAria: 'Need an Agent? Tell SandBase the result you need',
    announceName: 'SandBase Agent Lab',
    announceMessage: 'Need finished work? Tell us the result and we will map the Agent.',
    announceCta: 'Request an Agent',
    leaderboards: 'Leaderboards',
    allBoards: 'All leaderboards',
    skip: 'Skip to content',
  },
  footer: {
    home: 'SandBase home',
    blurb: 'Small experiments built on SandBase models, APIs and sandboxes.',
    explore: 'Explore',
    models: 'Models',
    apis: 'APIs',
    agents: 'Agents',
    build: 'Build',
    sandbox: 'Sandbox',
    docs: 'Docs',
    pricing: 'Pricing',
    lab: 'Lab',
    leaderboard: 'Leaderboard',
    myRuns: 'My runs',
    status: 'All systems operational',
    rights: (year: number): string => `© ${year} SandBase`,
  },
  scene: {
    'pelican-test': {
      unit: 'pelican', units: 'pelicans', ok: 'Draws something',
      classic: 'Pelican riding a bicycle', the: 'the Pelican Test',
      kinds: { static: 'Classic SVG', animated: 'Animated SVG' } as Record<string, string>,
    },
    'endless-runner': {
      unit: 'game', units: 'games', ok: 'Builds a game',
      classic: 'Neon city runner', the: 'the Endless Runner',
      kinds: { game: 'Game' } as Record<string, string>,
    },
    'watermelon-test': {
      unit: 'toy', units: 'toys', ok: 'Runs',
      classic: 'Watermelon rubber-band toy', the: 'the Watermelon Test',
      kinds: { toy: 'Toy' } as Record<string, string>,
    },
  } as Record<SceneSlug, { unit: string; units: string; ok: string; classic: string; the: string; kinds: Record<string, string> }>,
  common: {
    locale: 'en-US',
    loading: 'Loading…',
    tryAgain: 'Try again',
    retryLoad: 'Loading…',
    copy: 'Copy',
    copied: 'Copied',
    copyLink: 'Copy link',
    shareOnX: 'Share on X',
    sota: 'SOTA',
    model: 'Model',
    tester: 'Tester',
    waiting: 'Waiting',
    noScore: 'no score',
    retryAgain: 'Please try again.',
    retryMinute: 'Please try again in a minute.',
    models: (n: number): string => `${n} model${n === 1 ? '' : 's'}`,
    testers: (n: number): string => `${n} tester${n === 1 ? '' : 's'}`,
    score5: (s: number): string => `${s.toFixed(1)}/5`,
    kind: (k: string): string => (k === 'animated' ? 'Animated' : k === 'game' ? 'Game' : k === 'toy' ? 'Toy' : 'Classic'),
    badge: (label: string): string => label,
    runScene: (scene: SceneSlug): string => `Run ${theEn(scene)}`,
    publishedAt: (value: string | null | undefined): string => {
      if (!value) return 'Release date unavailable'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Release date unavailable'
      return `Released ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`
    },
    day: (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'),
    dateTime: (iso: string): string => new Date(iso).toLocaleString('en-US'),
    shareText: (r: ShareInput, scene: SceneSlug): string => {
      const who = modelLabel(r.model)
      if (scene === 'endless-runner') {
        if (!r.ok) return `${who} was asked to build an endless runner game and... wiped out. 🫠`
        return `${who} built an endless runner game${r.score != null ? ` and scored ${r.score.toFixed(1)}/5` : ''}. 🎮`
      }
      if (scene === 'watermelon-test') {
        if (!r.ok) return `${who} was asked to build a watermelon rubber-band toy and... it never ran. 🫠`
        return `${who} built a watermelon rubber-band toy${r.score != null ? ` and scored ${r.score.toFixed(1)}/5` : ''}. 🍉`
      }
      const what = r.classic ? 'a pelican riding a bicycle' : r.title.toLowerCase()
      if (!r.ok) return `${who} was asked to draw ${what} and... wiped out. 🫠`
      return `${who} drew ${what}${r.score != null ? ` and scored ${r.score.toFixed(1)}/5` : ''}. 🦩🚲`
    },
  },
  labIndex: {
    title: 'SandBase Lab: playful AI experiments you can run and share',
    eyebrow: 'SandBase Lab',
    displayLead: 'Small experiments.',
    displayMark: 'Big arguments.',
    lede: 'Playful tests built from SandBase models, sandboxes and APIs. Run them yourself, share the results, and settle which model is actually better.',
    sectionEyebrow: 'Experiments',
    sectionTitle: 'Pick an experiment',
    live: 'Live',
    playNow: 'Play now →',
    comingSoon: 'Coming soon',
    playableInside: 'Playable game inside →',
    altPelican: 'A pelican riding a bicycle, drawn by an AI model',
    altWatermelon: 'A watermelon toy built by an AI model, first look',
    stickerEmpty: 'Illustration · your model goes here',
    cards: {
      'pelican-test': {
        tagline: 'Every model draws a pelican riding a bicycle, in code.',
        description: 'Same prompt, one try. Rendered, judged by a model, ranked on a public leaderboard.',
        tags: ['LLM', 'SVG', 'Leaderboard'],
      },
      'endless-runner': {
        tagline: 'Every model builds a playable endless runner game, in one HTML file.',
        description: 'Same brief, one try. Play it in your browser, judged on code and collision, ranked on a public leaderboard.',
        tags: ['LLM', 'HTML5 game', 'Leaderboard'],
      },
      'watermelon-test': {
        tagline: 'Every model builds a 3D toy: snap rubber bands onto a watermelon until it bursts.',
        description: 'Same prompt, one try. A real browser clicks it, a vision judge scores the screenshots, ranked on a public leaderboard.',
        tags: ['LLM', 'Three.js', 'Leaderboard'],
      },
    } as Record<SceneSlug, { tagline: string; description: string; tags: string[] }>,
  },
  landing: {
    // Shared across the three landing pages.
    justTested: 'Just tested',
    topPelicans: 'Top pelicans',
    runYourOwn: 'Run your own',
    guessTheModel: 'Guess the model',
    seeLeaderboard: 'See the leaderboard',
    howItWorks: 'How it works',
    yourTurn: 'Your turn',
    leaderboardEyebrow: 'Leaderboard',
    fameEyebrow: 'Hall of Fame',
    shameEyebrow: 'Hall of Shame',
    miniGame: 'Mini game',
    faceOffEyebrow: 'Face-off',
    browseEyebrow: 'Browse',
    measured: 'Measured',
    judgeScale: 'Judge · 1–5',
    stealPrompt: 'Steal the prompt',
    stealBrief: 'Steal the brief',
    thePrompt: 'The prompt',
    theBrief: 'The brief',
    promptHint: 'Copy it, try it on any model, and see what you get.',
    openPelican: (model: string): string => `Open ${model}'s pelican`,
  },
  pelican: {
    title: 'The Pelican Test | SandBase Lab',
    loading: 'Loading pelicans…',
    errorTitle: 'The Pelican Test is taking a break',
    heroEyebrow: 'SandBase Lab · Nº 01',
    heroLead: 'The Pelican ',
    heroMark: 'Test',
    lede: 'We ask AI models to draw a pelican riding a bicycle, in code. Same prompt, one try, no cherry-picking. Some pedal. Some float. Some forget the bicycle.',
    statModels: 'models tested',
    statResults: 'pelicans drawn',
    statTesters: 'testers',
    statCost: 'avg per pelican',
    steps: [
      ['01', 'Same prompt, one try', 'Every model gets the identical prompt. No retries, no cherry-picking.'],
      ['02', 'Rendered on the spot', 'Each SVG is sanitised and rendered to an image the moment it arrives.'],
      ['03', 'Judged by a model', ''],
      ['04', 'Everything public', 'Images, SVG source, time and the real cost of every pelican.'],
    ] as Array<[string, string, string]>,
    judgedBy: (model: string): string => `${model} scores every drawing on the same rubric.`,
    boardTitle: 'Who draws the best pelican?',
    boardLede: 'Overall score averages one canonical Classic and Animated result per tester. Failed or unscored output counts as 0; successful retries replace prior failures.',
    fameTitle: 'Pelicans that actually ride',
    fameLede: "The judge's favorites, one per model.",
    shameTitle: 'Wipeouts, frozen wheels and abstract art',
    shameLede: 'The ones that forgot the bicycle, drew a cloud, or promised animation and never moved.',
    guessTitle: 'Guess the model',
    guessLede: 'Five pelicans, four names each. Share your score and see if your friends can tell Claude from Gemini.',
    faceOffTitle: 'Pick two models. Settle it.',
    faceOffLede: 'Their best classic and animated pelicans, side by side. The link keeps your matchup.',
    browseTitle: 'Every pelican, by model or by tester',
    browseLede: 'Open a model to see all of its attempts, or a tester to see everything they ran.',
    howTitle: 'One prompt. Every model. A public scoreboard.',
    howLede: 'The leaderboard, Hall of Fame, Guess the model and Face-off fill in as soon as the first pelicans land.',
    runTitleHas: 'Run your own Pelican Test',
    runTitleEmpty: 'Draw the first pelicans',
    runLede: 'Pick the models, keep the pelican or remix the subject. Billed to your SandBase account for the tokens actually used.',
    crumbs: ['Lab', 'Leaderboards'] as [string, string],
    kicker: 'SandBase Lab · Benchmark · Nº 01',
    h1Lead: 'Can your model draw ',
    h1Mark: 'a pelican on a bike?',
    chipClassic: 'Classic SVG',
    chipAnimated: 'Animated SVG',
    chipJudge: (model: string): string => `Judge · ${model}`,
    mediaCaption: (judge: string): string => `Same prompt, one try · judged by ${judge}`,
    topBadge: (model: string): string => `#1 · ${model}`,
    jump: { board: 'Leaderboard', fame: 'Hall of Fame', shame: 'Hall of Shame', guess: 'Guess', faceOff: 'Face-off', browse: 'Browse', how: 'How it works', prompt: 'Prompt', faq: 'FAQ', run: 'Run your own →' },
    jumpAria: 'On this page',
    podiumAria: 'Top three',
    podiumScore: 'Overall',
    podiumDraws: 'Draws',
    podiumMoves: 'Moves',
    podiumCost: 'Avg cost',
    verifiedMotion: 'VERIFIED MOTION',
    restOfBoard: 'The rest of the field',
    howEyebrow: 'How it works',
    howBandTitle: 'One prompt, one try, one judge',
    howBandLede: 'Nothing is retouched. Each drawing is the first SVG the model returned, rendered as is and scored on the same rubric.',
    scoredOn: 'What the judge scores',
    faqEyebrow: 'FAQ',
    faqTitle: 'Questions, answered',
    faqMore: 'Another question? Tell us',
    faq: [
      ['Why a pelican on a bicycle?', 'It is an odd request no model has memorised, so it shows how a model plans a scene in code: a bird that cannot ride, a bicycle with the right parts, and the two put together so the pelican actually rides.'],
      ['How is a drawing scored?', 'A vision model scores every rendered drawing from 1 to 5 on the same rubric (subject, action, structure, looks). Animated drawings are also checked for real motion. The overall score averages one Classic and one Animated result per tester; failed output counts as 0.'],
      ['Are the results cherry-picked?', 'No. Every model gets one try with the same prompt, and every result is published, including the wipeouts in the Hall of Shame.'],
      ['What does a run cost?', 'Each pelican shows the real cost read back from SandBase, drawing plus judging. Your own runs are billed to your SandBase account for the tokens the models actually use.'],
      ['Can I try the prompt elsewhere?', 'Yes. Copy it from the prompt section below and run it on any model; your pelican can be compared directly with the ones here.'],
    ] as Array<[string, string]>,
  },
  runner: {
    title: 'Endless Runner | SandBase Lab',
    loading: 'Loading games…',
    errorTitle: 'Endless Runner is taking a break',
    heroEyebrow: 'SandBase Lab · Nº 02',
    heroLead: 'Endless ',
    heroMark: 'Runner',
    lede: 'We ask AI models to build a playable endless runner game in a single HTML file. Same brief, one try, no cherry-picking. Some run. Some stumble. Some forget how to jump.',
    statModels: 'models tested',
    statResults: 'games built',
    statTesters: 'testers',
    statCost: 'avg per game',
    emptyPlayable: 'No playable game yet · see the leaderboard ↓',
    emptyNoGames: 'No games yet · build the first one ↓',
    steps: [
      ['01', 'Same brief, one try', 'Every model gets the identical game brief. No retries, no cherry-picking.'],
      ['02', 'Playable on the spot', 'Each HTML file runs right in your browser, sandboxed, the moment it arrives.'],
      ['03', 'Judged like code', ''],
      ['04', 'Everything public', 'Games, source, time and the real cost of every run.'],
    ] as Array<[string, string, string]>,
    judgedBy: (model: string): string => `${model} reviews the source and scores it on the same rubric.`,
    boardTitle: 'Who builds the best runner?',
    boardLede: 'Overall score averages one canonical game per tester. Failed or unscored output counts as 0; successful retries replace prior failures.',
    fameTitle: 'Runners that actually run',
    fameLede: "The judge's favorites, one per model. Open one and play it.",
    shameTitle: 'Wipeouts and broken jumps',
    shameLede: 'The ones that never boot, forgot collision, or call an API that does not exist.',
    browseTitle: 'Every game, by model or by tester',
    browseLede: 'Open a model to see all of its attempts, or a tester to see everything they ran.',
    howTitle: 'One brief. Every model. A public scoreboard.',
    howLede: 'The leaderboard and Hall of Fame fill in as soon as the first games land.',
    runTitleHas: 'Run your own Endless Runner',
    runTitleEmpty: 'Build the first games',
    runLede: 'Pick the models, keep the neon city or remix the subject. Billed to your SandBase account for the tokens actually used.',
  },
  watermelon: {
    title: 'The Watermelon Test | SandBase Lab',
    loading: 'Loading watermelons…',
    errorTitle: 'The Watermelon Test is taking a break',
    method: 'The first official results were generated with real model calls; their screenshots were rendered in a local headless Chrome during development.',
    heroEyebrow: 'SandBase Lab · Nº 03',
    heroLead: 'The Watermelon ',
    heroMark: 'Test',
    lede: 'We ask AI models to build a 3D toy in one HTML file: snap rubber bands onto a watermelon until it bursts. Same prompt, one try. Some squeeze. Some splatter. Some never load.',
    statModels: 'models tested',
    statResults: 'toys built',
    statTesters: 'testers',
    statCost: 'avg per toy',
    runYourOwnSoon: 'Run your own · coming soon',
    coverAlt: (model: string): string => `${model}: watermelon toy, first look`,
    screenshotsBelow: 'Screenshots below ↓',
    stickerEmpty: 'Your model goes here',
    steps: [
      ['01', 'Same prompt, one try', 'Every model gets the identical prompt for a single-file Three.js toy. No retries, no cherry-picking.'],
      ['02', 'Clicked by a real browser', 'Headless Chrome loads the page, snaps bands on with scripted clicks and takes three screenshots.'],
      ['03', 'Judged on what it shows', ''],
      ['04', 'Everything public', 'Screenshots, the playable page, time and the real cost of every run.'],
    ] as Array<[string, string, string]>,
    judgedBy: (model: string): string => `${model} scores the screenshots together with the automated checks.`,
    boardTitle: 'Who builds the best watermelon?',
    boardLede: 'Overall score averages one canonical toy per tester. Failed or unscored output counts as 0; successful retries replace prior failures.',
    fameTitle: 'Watermelons that actually burst',
    fameLede: "The judge's favorites, one per model. Open one to see every screenshot and play it.",
    shameTitle: 'Duds and blank screens',
    shameLede: 'The ones that never load, never count a band, or never burst.',
    browseTitle: 'Every toy, by model or by tester',
    browseLede: 'Open a model to see all of its attempts, or a tester to see everything they ran.',
    methodEyebrow: 'Method',
    howTitle: 'One prompt. A real browser. A vision judge.',
    runTitle: 'Run your own Watermelon Test',
    runLedeHas: 'Pick the models. Billed to your SandBase account for the tokens actually used.',
    runLedeOfficial: 'Official results only for now.',
  },
  run: {
    titleSuffix: 'run | SandBase Lab',
    notFoundTitle: 'Run not found',
    notFoundMsg: 'It may be private. Sign in with the account that started it.',
    step: { pending: 'Waiting', drawing: 'Drawing', rendering: 'Rendering', judging: 'Judging', done: 'Done', failed: 'Failed' } as Record<string, string>,
    titleDone: (units: string): string => `The ${units} are in.`,
    titleFailed: 'This run did not finish.',
    titleMaking: (units: string): string => `Your ${units} are being made…`,
    loading: (units: string): string => `Loading ${units}…`,
    byline: (title: string, models: number): string => `${title} · ${models} model${models === 1 ? '' : 's'} · by `,
    bylineEnd: '.',
    activeTail: (done: number, total: number): string => ` ${done}/${total} finished. This takes a few minutes; keep this tab open until it finishes.`,
    doneTail: (cost: string): string => ` Total cost ${cost}.`,
    progressAria: 'Run progress',
    resultsTitle: 'Results',
    stepsTitle: 'Progress',
  },
  result: {
    builtRunner: 'built a runner',
    builtToy: 'built a watermelon toy',
    drew: (what: string): string => `drew ${what}`,
    pelicanOnBike: 'a pelican riding a bicycle',
    wipedOut: 'wiped out',
    notFound: (unitCap: string): string => `${unitCap} not found`,
    notPublic: (unit: string): string => `This ${unit} is not public.`,
    testedBy: 'Tested by ',
    sameAs: (game: boolean): string => `. Same ${game ? 'brief' : 'prompt'} as everyone, one try, no retries.`,
    gameHint: 'Click the game, then press',
    gameHintJump: 'to jump and',
    gameHintSlide: 'to slide.',
    toyHint: 'Click the watermelon to snap on a band. The screenshots below are what the judge saw.',
    factScore: 'Score',
    factCost: 'Cost',
    factBuildJudge: 'Build + judge',
    factTime: 'Time',
    factMotion: 'Motion',
    motionNa: 'n/a',
    motionYes: 'yes',
    motionNone: 'none',
    downloadSvg: 'Download SVG',
    downloadHtml: (unit: string): string => `Download ${unit} HTML`,
    runYourOwn: 'Run your own',
    moreFromModel: (model: string): string => `More from ${model}`,
    moreFromAuthor: (author: string): string => `More from ${author}`,
    framesEyebrow: 'What the judge saw',
    framesTitle: 'Three screenshots from the automated browser',
    scorecard: "The judge's scorecard",
    scorecardLede: (judge: string): string => `Scored by ${judge} on the same rubric as every other result.`,
    checks: 'Automatic checks',
    comment: 'Judge comment',
    sourceTitle: 'Source and sharing',
    moreTitle: (model: string): string => `More from ${model}`,
    seeAll: 'See all',
    rankOf: (rank: number): string => `#${rank} on the leaderboard`,
  },
  model: {
    notFoundTitle: 'Model not found',
    notFoundMsg: 'Pick a model from the leaderboard.',
    profileTitle: (model: string, scene: string): string => `${model} on ${scene} | SandBase Lab`,
    backLeaderboard: '← Leaderboard',
    firstTested: (day: string): string => `first tested ${day}`,
    everyFrom: (unit: string, model: string): string => `Every ${unit} from ${model}`,
    oneTryPerRun: 'One try per run, from every tester.',
    testersEyebrow: 'Testers',
    whoTested: 'Who tested it',
    whoTestedLede: 'Click a tester to see everything they ran.',
  },
  skills: {
    eyebrow: 'Skills',
    title: 'This model with Skills',
    lede: 'The same model running Lab Skills on the pelican theme.',
    open: 'See the pelican comparison →',
    failed: 'Run failed',
    skipped: 'Not run',
    unavailable: 'Model unavailable',
    cost: (cost: string): string => `cost ${cost}`,
    posterAlt: (skill: string): string => `${skill} pelican run poster`,
  },
  tester: {
    title: (author: string, units: string): string => `${author}'s ${units} | SandBase Lab`,
    backTesters: '← Testers',
    since: (day: string): string => `Tester · since ${day}`,
    everythingRan: (author: string): string => `Everything ${author} ran`,
    everythingLede: 'Every model, every run, best first.',
    modelsEyebrow: 'Models',
    modelsTested: 'Models tested',
    modelsLede: 'Click a model to compare with other testers.',
  },
  profile: {
    rank: 'Rank',
    overall: 'Overall score',
    actuallyMoves: 'Actually moves',
    avgCost: 'Avg cost',
    runs: 'Runs',
    lastRun: 'Last run',
    testerCol: 'Tester',
    modelCol: 'Model',
    noPublicYet: (units: string): string => `No public ${units} yet.`,
  },
  browse: {
    typeAria: 'Type',
    all: 'All',
    classic: 'Classic',
    animated: 'Animated',
    sort: 'Sort',
    bestFirst: 'Best first',
    wildestFirst: 'Wildest first',
    newest: 'Newest',
    cheapest: 'Cheapest',
    loadingUnits: (units: string): string => `Loading ${units}…`,
    count: (total: number, units: string): string => `${total} ${units}`,
    couldNotLoad: (units: string): string => `Could not load ${units}.`,
    noneHere: (units: string): string => `No ${units} here yet.`,
    showMore: (units: string): string => `Show more ${units}`,
    byAria: 'Browse by',
    byModel: (n: number): string => `By model · ${n}`,
    byTester: (n: number): string => `By tester · ${n}`,
    tabByModel: 'By model',
    tabByTester: 'By tester',
    tabEverything: 'Everything',
    allTypes: 'All types',
    models: 'Models',
    testers: 'Testers',
    loadingPelicans: 'Loading pelicans…',
    pelicanCount: (total: number): string => `${total} pelican${total === 1 ? '' : 's'}`,
    couldNotLoadPelicans: 'Could not load pelicans.',
    noPelicans: 'No pelicans here yet. Run the test and yours will be the first.',
    showMorePelicans: 'Show more pelicans',
  },
  myRuns: {
    title: 'My runs | SandBase Lab',
    heading: 'My runs',
    lede: (name: string): string => `Every ${name} run you started with this SandBase account.`,
    signInTitle: 'Sign in to see your runs.',
    signIn: 'Sign in',
    loadError: 'Could not load your runs.',
    loading: 'Loading your runs…',
    noneTitle: 'No runs yet.',
    noneLede: (units: string): string => `Pick a few models and run your first ${units}.`,
    status: { running: 'Running', succeeded: 'Finished', failed: 'Did not finish' } as Record<string, string>,
    best: (model: string, score: string): string => `Best ${model} ${score}`,
    byline: (dateTime: string, models: number, author: string): string => `${dateTime} · ${models} model${models === 1 ? '' : 's'} · by ${author}`,
  },
  media: {
    pelicanTitle: 'pelican riding a bicycle',
    noPelican: 'No pelican',
    noUnit: (unit: string): string => `No ${unit}`,
    noToy: 'No toy',
    animation: 'animation',
    playable: (unit: string): string => `playable ${unit}`,
    frameCaptions: ['First look', 'After 20 bands', 'The burst'],
    frameFallback: (n: number): string => `Frame ${n}`,
    screenshotsAria: 'Screenshots from the automated browser',
    clickToPlay: 'Click to play',
    clickToPlayAria: (label: string, unit: string, score: number | null): string => `Click to play ${label}'s ${unit}${score != null ? `, top score ${score.toFixed(1)} out of 5` : ''}`,
    topScore: (score: string): string => `Top score · ${score}`,
    topPick: 'Top pick',
    by: (author: string): string => `by ${author}`,
    perUnit: (unit: string): string => `per ${unit}`,
    cardAria: (model: string, kind: string, score: number | null): string => `${model}, ${kind}${score != null ? `, scored ${score.toFixed(1)} out of 5` : ''}`,
    openDetails: (label: string): string => `${label}. Open details`,
    nothingYet: 'Nothing here yet.',
    noUnitsYet: (units: string): string => `No ${units} yet. The first run will appear here.`,
    leaderboardAria: (name: string): string => `${name} leaderboard`,
    rank: 'Rank',
    model: 'Model',
    overall: 'Overall score',
    actuallyMoves: 'Actually moves',
    avgCost: 'Avg cost',
    newPill: 'New',
  },
  games: {
    guessLocked: 'The game unlocks once four models have drawn a pelican.',
    verdicts: [
      'Did the pelicans all look the same?',
      'A pelican novice.',
      'Getting the hang of it.',
      'Sharp eye.',
      'Model whisperer.',
      'Perfect. You might be a judge model.',
    ],
    yourScore: 'Your score',
    shareGuess: (score: number, total: number): string => `I guessed ${score}/${total} AI pelicans on the SandBase Pelican Test. Can you beat me?`,
    playAgain: 'Play again',
    round: (i: number, total: number, score: number): string => `Round ${i} of ${total} · score ${score}`,
    whichModel: 'Which model drew this?',
    pickModel: 'Pick a model',
    correct: 'Correct',
    nope: 'Nope',
    nice: 'Nice.',
    itWas: (model: string): string => `It was ${model}.`,
    judgeQuote: (comment: string): string => `Judge: “${comment}”`,
    seeScore: 'See my score',
    nextPelican: 'Next pelican',
    faceOffLocked: 'Face-offs unlock once two models are on the board.',
    leftCorner: 'Left corner',
    rightCorner: 'Right corner',
    openSide: (model: string, kind: string): string => `Open ${model} ${kind} pelican`,
    noKindYet: (animated: boolean): string => `No ${animated ? 'animated' : 'classic'} pelican yet`,
    draws: 'Draws',
    winner: 'Winner',
    beats: (winner: string, loser: string): string => `${modelLabel(winner)} beats ${modelLabel(loser)} on the Pelican Test.`,
    versus: (a: string, b: string): string => `${modelLabel(a)} vs ${modelLabel(b)} on the Pelican Test. Who drew it better?`,
  },
  runPanel: {
    comingSoon: 'Coming soon',
    comingSoonTitle: (scene: SceneSlug): string => `Running ${theEn(scene)} yourself is coming soon.`,
    comingSoonBody: 'For now SandBase Lab runs it officially; the results are above.',
    signInTitle: (name: string): string => `Sign in to run your own ${name}.`,
    signInBody: (max: number, remixable: boolean, unit: string, units: string): string =>
      `Use your SandBase account. Pick up to ${max} models${remixable ? `, keep the classic ${unit} or remix the subject` : ''}. You pay only for the tokens the models actually use, and your ${units} join the wall.`,
    signInCta: 'Sign in to run it',
    modelsLabel: (picked: number, max: number): string => `Models · ${picked}/${max}`,
    clear: 'Clear',
    searchModels: 'Search models',
    remove: (model: string): string => `Remove ${model}`,
    availableModels: 'Available models',
    loadingModels: 'Loading models…',
    modelsUnavailable: 'Models are unavailable right now.',
    tests: 'Tests',
    subjectLabel: 'Subject (English)',
    subjectKeep: (unit: string): string => `Keep the classic ${unit} to land on the leaderboard.`,
    subjectRemix: 'Remixes appear in Explore but not on the leaderboard.',
    subjectInvalid: 'Use plain English, 3–120 characters.',
    nameLabel: 'Display name (English, optional)',
    namePlaceholder: 'Anonymous builder',
    nameInvalid: '2–32 characters: letters, digits, space, dot, dash or underscore.',
    worstCase: (count: number, units: string): string => `Worst case (${count} ${units} + judging)`,
    billing: (max: string, daily: number): string => `Billed to your SandBase account at normal model prices for the tokens actually used, usually far less. Limit ${max} per run, ${daily} runs a day. Keep the page open until it finishes.`,
    topUp: 'Top up',
    signInAgain: 'Sign in again',
    starting: 'Starting…',
    runScene: (scene: SceneSlug): string => `Run ${theEn(scene)}`,
    couldNotStart: 'Could not start the run.',
  },
  compare: {
    rendering: 'Rendering your comparison reel…',
    ready: 'Your comparison video is ready.',
    videoError: 'Could not create the video.',
    posterDownloaded: 'Comparison poster downloaded.',
    posterError: 'Could not create the poster.',
    eyebrow: 'Compare video',
    title: 'Turn this matchup into a reel.',
    scenes: (n: number): string => `${n} scene${n === 1 ? '' : 's'} · 1280×720 · about ${n * 2.8}s · created locally in your browser.`,
    needPelican: 'These models need at least one completed pelican before a reel can be made.',
    creating: 'Creating video…',
    createVideo: 'Create comparison video',
    downloadPoster: 'Download poster',
    generatedAria: 'Generated comparison video',
    downloadWebm: 'Download WebM',
    noWebm: 'This browser cannot make WebM recordings, but the PNG poster download remains available.',
  },
  art: {
    pelicanAria: 'Illustration: a pelican riding a bicycle',
  },
  nav: {
    backTo: (name: string): string => `Back to ${name}`,
    pageNotFound: 'Page not found',
    pageNotFoundMsg: 'This Lab page does not exist.',
    notFoundTitle: 'Not found | SandBase Lab',
  },
}

export type Dict = typeof en

const zhCN: Dict = {
  header: {
    myRuns: '我的运行',
    signIn: '登录',
    start: '开始构建',
    console: '控制台',
    menu: '菜单',
    close: '关闭',
    switchScene: '切换场景',
    scene: '场景',
    primaryNav: '主导航',
    mobileNav: '移动端导航',
    closeMenu: '关闭菜单',
    home: 'SandBase 首页',
    announceAria: '需要 Agent？告诉 SandBase 你想要的结果',
    announceName: 'SandBase Agent Lab',
    announceMessage: '需要做完的活儿？说出你要的结果，我们来设计 Agent。',
    announceCta: '提交 Agent 需求',
    leaderboards: '榜单',
    allBoards: '全部榜单',
    skip: '跳到内容',
  },
  footer: {
    home: 'SandBase 首页',
    blurb: '基于 SandBase 模型、API 和沙箱构建的小实验。',
    explore: '探索',
    models: '模型',
    apis: 'API',
    agents: 'Agent',
    build: '构建',
    sandbox: '沙箱',
    docs: '文档',
    pricing: '价格',
    lab: 'Lab',
    leaderboard: '榜单',
    myRuns: '我的运行',
    status: '所有系统运行正常',
    rights: (year: number): string => `© ${year} SandBase`,
  },
  scene: {
    'pelican-test': {
      unit: '鹈鹕', units: '鹈鹕', ok: '能画出东西',
      classic: '骑自行车的鹈鹕', the: 'Pelican Test',
      kinds: { static: '经典 SVG', animated: '动画 SVG' },
    },
    'endless-runner': {
      unit: '游戏', units: '游戏', ok: '能做出游戏',
      classic: '霓虹城市跑酷', the: 'Endless Runner',
      kinds: { game: '游戏' },
    },
    'watermelon-test': {
      unit: '玩具', units: '玩具', ok: '能运行',
      classic: '西瓜皮筋玩具', the: 'Watermelon Test',
      kinds: { toy: '玩具' },
    },
  },
  common: {
    locale: 'zh-CN',
    loading: '加载中…',
    tryAgain: '重试',
    retryLoad: '加载中…',
    copy: '复制',
    copied: '已复制',
    copyLink: '复制链接',
    shareOnX: '分享到 X',
    sota: 'SOTA',
    model: '模型',
    tester: '测试者',
    waiting: '等待中',
    noScore: '暂无评分',
    retryAgain: '请稍后重试。',
    retryMinute: '请一分钟后重试。',
    models: (n: number): string => `${n} 个模型`,
    testers: (n: number): string => `${n} 位测试者`,
    score5: (s: number): string => `${s.toFixed(1)}/5`,
    kind: (k: string): string => (k === 'animated' ? '动画' : k === 'game' ? '游戏' : k === 'toy' ? '玩具' : '经典'),
    badge: (label: string): string => {
      const map: Record<string, string> = { Wipeout: '翻车', Frozen: '静止', Masterpiece: '神作', 'Hall of Fame': '名人堂', 'Abstract art': '抽象艺术' }
      return map[label] ?? label
    },
    runScene: (scene: SceneSlug): string => `运行 ${brand(scene)}`,
    publishedAt: (value: string | null | undefined): string => {
      if (!value) return '发布日期未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '发布日期未知'
      return `发布于 ${date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`
    },
    day: (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'),
    dateTime: (iso: string): string => new Date(iso).toLocaleString('zh-CN'),
    shareText: (r: ShareInput, scene: SceneSlug): string => {
      const who = modelLabel(r.model)
      if (scene === 'endless-runner') {
        if (!r.ok) return `${who} 被要求做一个无尽跑酷游戏，结果……翻车了。🫠`
        return `${who} 做出了一个无尽跑酷游戏${r.score != null ? `，拿到 ${r.score.toFixed(1)}/5` : ''}。🎮`
      }
      if (scene === 'watermelon-test') {
        if (!r.ok) return `${who} 被要求做一个西瓜皮筋玩具，结果……根本没跑起来。🫠`
        return `${who} 做出了一个西瓜皮筋玩具${r.score != null ? `，拿到 ${r.score.toFixed(1)}/5` : ''}。🍉`
      }
      const what = r.classic ? '一只骑自行车的鹈鹕' : r.title.toLowerCase()
      if (!r.ok) return `${who} 被要求画 ${what}，结果……翻车了。🫠`
      return `${who} 画了 ${what}${r.score != null ? `，拿到 ${r.score.toFixed(1)}/5` : ''}。🦩🚲`
    },
  },
  labIndex: {
    title: 'SandBase Lab：可以运行和分享的趣味 AI 实验',
    eyebrow: 'SandBase Lab',
    displayLead: '小实验。',
    displayMark: '大争论。',
    lede: '用 SandBase 模型、沙箱和 API 打造的趣味测试。亲手运行、分享结果，看看到底哪个模型更强。',
    sectionEyebrow: '实验',
    sectionTitle: '挑一个实验',
    live: '上线',
    playNow: '立即体验 →',
    comingSoon: '敬请期待',
    playableInside: '内含可玩游戏 →',
    altPelican: 'AI 模型画的一只骑自行车的鹈鹕',
    altWatermelon: 'AI 模型做的西瓜玩具，初始画面',
    stickerEmpty: '插画 · 这里放你的模型',
    cards: {
      'pelican-test': {
        tagline: '每个模型都用代码画一只骑自行车的鹈鹕。',
        description: '同一道提示，一次机会。渲染出来、由模型评分，登上公开榜单。',
        tags: ['LLM', 'SVG', '榜单'],
      },
      'endless-runner': {
        tagline: '每个模型都在一个 HTML 文件里做一个可玩的无尽跑酷游戏。',
        description: '同一份需求，一次机会。在浏览器里直接玩，按代码和碰撞评分，登上公开榜单。',
        tags: ['LLM', 'HTML5 游戏', '榜单'],
      },
      'watermelon-test': {
        tagline: '每个模型都做一个 3D 玩具：往西瓜上套皮筋，直到它炸开。',
        description: '同一道提示，一次机会。真实浏览器点击操作，视觉评审给截图打分，登上公开榜单。',
        tags: ['LLM', 'Three.js', '榜单'],
      },
    },
  },
  landing: {
    justTested: '刚刚测试',
    topPelicans: '最佳鹈鹕',
    runYourOwn: '自己来运行',
    guessTheModel: '猜模型',
    seeLeaderboard: '查看榜单',
    howItWorks: '运作方式',
    yourTurn: '轮到你了',
    leaderboardEyebrow: '榜单',
    fameEyebrow: '名人堂',
    shameEyebrow: '翻车堂',
    miniGame: '小游戏',
    faceOffEyebrow: '对决',
    browseEyebrow: '浏览',
    measured: '实测',
    judgeScale: '评审 · 1–5',
    stealPrompt: '拿走提示词',
    stealBrief: '拿走需求',
    thePrompt: '提示词',
    theBrief: '需求',
    promptHint: '复制下来，在任意模型上试试，看看会得到什么。',
    openPelican: (model: string): string => `查看 ${model} 的鹈鹕`,
  },
  pelican: {
    title: 'The Pelican Test | SandBase Lab',
    loading: '正在加载鹈鹕…',
    errorTitle: 'The Pelican Test 暂时休息一下',
    heroEyebrow: 'SandBase Lab · Nº 01',
    heroLead: 'The Pelican ',
    heroMark: 'Test',
    lede: '我们让 AI 模型用代码画一只骑自行车的鹈鹕。同一道提示，一次机会，不挑结果。有的在蹬车，有的在飘，有的忘了自行车。',
    statModels: '已测模型',
    statResults: '已画鹈鹕',
    statTesters: '测试者',
    statCost: '每只均价',
    steps: [
      ['01', '同一道提示，一次机会', '每个模型拿到完全相同的提示。不重试、不挑结果。'],
      ['02', '即时渲染', '每份 SVG 一到达就立即清洗并渲染成图片。'],
      ['03', '由模型评分', ''],
      ['04', '一切公开', '图片、SVG 源码、耗时和每只鹈鹕的真实成本。'],
    ],
    judgedBy: (model: string): string => `${model} 用同一套标准为每幅画打分。`,
    boardTitle: '谁画的鹈鹕最好？',
    boardLede: '总分取每位测试者各一份经典与动画的标准结果的平均值。失败或未评分的结果记 0 分；成功的重试会替换此前的失败。',
    fameTitle: '真能骑车的鹈鹕',
    fameLede: '评审的最爱，每个模型一份。',
    shameTitle: '翻车、卡死的轮子和抽象艺术',
    shameLede: '那些忘了自行车、画成一朵云，或者承诺动画却纹丝不动的作品。',
    guessTitle: '猜模型',
    guessLede: '五只鹈鹕，每只四个候选名字。分享你的成绩，看看朋友能否分清 Claude 和 Gemini。',
    faceOffTitle: '选两个模型，一决高下。',
    faceOffLede: '它们最好的经典与动画鹈鹕并排对比。链接会保留你的对阵。',
    browseTitle: '按模型或测试者浏览每只鹈鹕',
    browseLede: '打开一个模型查看它的全部尝试，或打开一位测试者查看他们运行过的一切。',
    howTitle: '一道提示。所有模型。一个公开榜单。',
    howLede: '只要第一批鹈鹕到位，榜单、名人堂、猜模型和对决就会陆续填满。',
    runTitleHas: '运行你自己的 Pelican Test',
    runTitleEmpty: '画出第一批鹈鹕',
    runLede: '选择模型，保留鹈鹕或改编主题。按实际用量从你的 SandBase 账户计费。',
    crumbs: ['Lab', '榜单'] as [string, string],
    kicker: 'SandBase Lab · 评测 · Nº 01',
    h1Lead: '你的模型能画出',
    h1Mark: '骑自行车的鹈鹕吗？',
    chipClassic: '经典 SVG',
    chipAnimated: '动画 SVG',
    chipJudge: (model: string): string => `评分 · ${model}`,
    mediaCaption: (judge: string): string => `同一道提示，一次机会 · 由 ${judge} 评分`,
    topBadge: (model: string): string => `第 1 名 · ${model}`,
    jump: { board: '排行榜', fame: '名人堂', shame: '翻车现场', guess: '猜模型', faceOff: '对决', browse: '浏览', how: '工作原理', prompt: '提示词', faq: '常见问题', run: '自己跑一次 →' },
    jumpAria: '本页目录',
    podiumAria: '前三名',
    podiumScore: '总分',
    podiumDraws: '画出',
    podiumMoves: '会动',
    podiumCost: '平均费用',
    verifiedMotion: '已验证动画',
    restOfBoard: '其余模型',
    howEyebrow: '工作原理',
    howBandTitle: '一道提示，一次机会，一个评委',
    howBandLede: '不做任何修饰。每张图都是模型返回的第一份 SVG，原样渲染，用同一套标准打分。',
    scoredOn: '评委看什么',
    faqEyebrow: '常见问题',
    faqTitle: '常见问题',
    faqMore: '还有问题？告诉我们',
    faq: [
      ['为什么是骑自行车的鹈鹕？', '这是个没有模型背过的古怪要求，能看出模型怎样用代码规划一个场景：一只不会骑车的鸟、零件齐全的自行车，再把两者放在一起，让鹈鹕真的在骑。'],
      ['怎么打分？', '一个视觉模型用同一套标准（主体、动作、结构、观感）给每张渲染后的图打 1 到 5 分。动画版还会检查是否真的在动。总分取每位测试者一张经典版和一张动画版的平均，失败的输出计 0 分。'],
      ['结果是挑过的吗？', '没有。每个模型用同一道提示只有一次机会，所有结果都会公开，包括翻车现场里的那些。'],
      ['跑一次要多少钱？', '每只鹈鹕都显示从 SandBase 读回的真实费用，包括画图和评分。你自己的运行按模型实际使用的 token 计入你的 SandBase 账户。'],
      ['可以在别处试这道提示吗？', '可以。从下方的提示词区复制，在任何模型上运行，画出来的鹈鹕可以直接和这里的比较。'],
    ] as Array<[string, string]>,
  },
  runner: {
    title: 'Endless Runner | SandBase Lab',
    loading: '正在加载游戏…',
    errorTitle: 'Endless Runner 暂时休息一下',
    heroEyebrow: 'SandBase Lab · Nº 02',
    heroLead: 'Endless ',
    heroMark: 'Runner',
    lede: '我们让 AI 模型在单个 HTML 文件里做一个可玩的无尽跑酷游戏。同一份需求，一次机会，不挑结果。有的能跑，有的踉跄，有的忘了怎么跳。',
    statModels: '个模型已测试',
    statResults: '个游戏已构建',
    statTesters: '位测试者',
    statCost: '每个游戏均价',
    emptyPlayable: '暂无可玩游戏 · 查看榜单 ↓',
    emptyNoGames: '还没有游戏 · 来做第一个 ↓',
    steps: [
      ['01', '同一份需求，一次机会', '每个模型拿到完全相同的游戏需求。不重试、不挑结果。'],
      ['02', '即时可玩', '每个 HTML 文件一到达就在你的浏览器里沙箱运行。'],
      ['03', '像代码一样评审', ''],
      ['04', '一切公开', '游戏、源码、耗时和每次运行的真实成本。'],
    ],
    judgedBy: (model: string): string => `${model} 审阅源码，并用同一套标准打分。`,
    boardTitle: '谁做的跑酷最好？',
    boardLede: '总分取每位测试者一份标准游戏的平均值。失败或未评分的结果记 0 分；成功的重试会替换此前的失败。',
    fameTitle: '真能跑起来的跑酷',
    fameLede: '评审的最爱，每个模型一份。打开就能玩。',
    shameTitle: '翻车和失灵的跳跃',
    shameLede: '那些根本启动不了、忘了碰撞检测，或调用了不存在的 API 的作品。',
    browseTitle: '按模型或测试者浏览每个游戏',
    browseLede: '打开一个模型查看它的全部尝试，或打开一位测试者查看他们运行过的一切。',
    howTitle: '一份需求。所有模型。一个公开榜单。',
    howLede: '只要第一批游戏到位，榜单和名人堂就会陆续填满。',
    runTitleHas: '运行你自己的 Endless Runner',
    runTitleEmpty: '做出第一批游戏',
    runLede: '选择模型，保留霓虹城市或改编主题。按实际用量从你的 SandBase 账户计费。',
  },
  watermelon: {
    title: 'The Watermelon Test | SandBase Lab',
    loading: '正在加载西瓜…',
    errorTitle: 'The Watermelon Test 暂时休息一下',
    method: '首批官方结果由真实的模型调用生成；其截图在开发期间由本地无头 Chrome 渲染。',
    heroEyebrow: 'SandBase Lab · Nº 03',
    heroLead: 'The Watermelon ',
    heroMark: 'Test',
    lede: '我们让 AI 模型在一个 HTML 文件里做一个 3D 玩具：往西瓜上套皮筋，直到它炸开。同一道提示，一次机会。有的在挤压，有的四溅，有的根本加载不出来。',
    statModels: '个模型已测试',
    statResults: '个玩具已构建',
    statTesters: '位测试者',
    statCost: '每个玩具均价',
    runYourOwnSoon: '自己来运行 · 即将推出',
    coverAlt: (model: string): string => `${model}：西瓜玩具，初始画面`,
    screenshotsBelow: '截图见下方 ↓',
    stickerEmpty: '这里放你的模型',
    steps: [
      ['01', '同一道提示，一次机会', '每个模型拿到完全相同的提示，做一个单文件 Three.js 玩具。不重试、不挑结果。'],
      ['02', '真实浏览器点击', '无头 Chrome 加载页面，用脚本点击套上皮筋，并拍下三张截图。'],
      ['03', '按呈现效果评审', ''],
      ['04', '一切公开', '截图、可玩页面、耗时和每次运行的真实成本。'],
    ],
    judgedBy: (model: string): string => `${model} 结合自动检查为这些截图打分。`,
    boardTitle: '谁做的西瓜最好？',
    boardLede: '总分取每位测试者一份标准玩具的平均值。失败或未评分的结果记 0 分；成功的重试会替换此前的失败。',
    fameTitle: '真能炸开的西瓜',
    fameLede: '评审的最爱，每个模型一份。打开可查看每张截图并亲自体验。',
    shameTitle: '哑弹和白屏',
    shameLede: '那些根本加载不出来、数不到一根皮筋，或永远炸不开的作品。',
    browseTitle: '按模型或测试者浏览每个玩具',
    browseLede: '打开一个模型查看它的全部尝试，或打开一位测试者查看他们运行过的一切。',
    methodEyebrow: '方法',
    howTitle: '一道提示。一个真实浏览器。一位视觉评审。',
    runTitle: '运行你自己的 Watermelon Test',
    runLedeHas: '选择模型。按实际用量从你的 SandBase 账户计费。',
    runLedeOfficial: '目前仅提供官方结果。',
  },
  run: {
    titleSuffix: '运行 | SandBase Lab',
    notFoundTitle: '找不到该运行',
    notFoundMsg: '它可能是私有的。请用发起它的账户登录。',
    step: { pending: '等待中', drawing: '绘制中', rendering: '渲染中', judging: '评分中', done: '完成', failed: '失败' },
    titleDone: (units: string): string => `${units}出炉了。`,
    titleFailed: '本次运行未完成。',
    titleMaking: (units: string): string => `你的${units}正在制作中…`,
    loading: (units: string): string => `正在加载${units}…`,
    byline: (title: string, models: number): string => `${title} · ${models} 个模型 · 由 `,
    bylineEnd: ' 运行。',
    activeTail: (done: number, total: number): string => ` 已完成 ${done}/${total}。这需要几分钟，完成前请保持本标签页打开。`,
    doneTail: (cost: string): string => ` 总成本 ${cost}。`,
    progressAria: '运行进度',
    resultsTitle: '结果',
    stepsTitle: '进度',
  },
  result: {
    builtRunner: '做了一个跑酷游戏',
    builtToy: '做了一个西瓜玩具',
    drew: (what: string): string => `画了 ${what}`,
    pelicanOnBike: '一只骑自行车的鹈鹕',
    wipedOut: '翻车了',
    notFound: (unitCap: string): string => `找不到该${unitCap}`,
    notPublic: (unit: string): string => `该${unit}未公开。`,
    testedBy: '测试者：',
    sameAs: (game: boolean): string => `。和大家一样的${game ? '需求' : '提示'}，一次机会，不重试。`,
    gameHint: '点击游戏，然后按',
    gameHintJump: '跳跃，按',
    gameHintSlide: '下滑。',
    toyHint: '点击西瓜套上一根皮筋。下方的截图就是评审看到的画面。',
    factScore: '评分',
    factCost: '成本',
    factBuildJudge: '构建 + 评分',
    factTime: '耗时',
    factMotion: '动态',
    motionNa: '不适用',
    motionYes: '有',
    motionNone: '无',
    downloadSvg: '下载 SVG',
    downloadHtml: (unit: string): string => `下载${unit} HTML`,
    runYourOwn: '自己来运行',
    moreFromModel: (model: string): string => `更多来自 ${model}`,
    moreFromAuthor: (author: string): string => `更多来自 ${author}`,
    framesEyebrow: '评审看到的画面',
    framesTitle: '来自自动化浏览器的三张截图',
    scorecard: '评委打分',
    scorecardLede: (judge: string): string => `由 ${judge} 按与其他结果相同的标准打分。`,
    checks: '自动检查',
    comment: '评委评语',
    sourceTitle: '源文件与分享',
    moreTitle: (model: string): string => `${model} 的更多作品`,
    seeAll: '查看全部',
    rankOf: (rank: number): string => `排行榜第 ${rank} 名`,
  },
  model: {
    notFoundTitle: '找不到该模型',
    notFoundMsg: '从榜单里挑一个模型。',
    profileTitle: (model: string, scene: string): string => `${model} 在 ${scene} | SandBase Lab`,
    backLeaderboard: '← 榜单',
    firstTested: (day: string): string => `首次测试于 ${day}`,
    everyFrom: (unit: string, model: string): string => `${model} 的全部${unit}`,
    oneTryPerRun: '每次运行一次机会，来自每位测试者。',
    testersEyebrow: '测试者',
    whoTested: '谁测试过它',
    whoTestedLede: '点击某位测试者查看他们运行过的一切。',
  },
  skills: {
    eyebrow: 'Skill',
    title: '这个模型跑 Skill',
    lede: '同一个模型用 Lab 的 Skill 做鹈鹕主题作品。',
    open: '查看鹈鹕对比 →',
    failed: '运行失败',
    skipped: '未运行',
    unavailable: '模型暂不可用',
    cost: (cost: string): string => `花费 ${cost}`,
    posterAlt: (skill: string): string => `${skill} 鹈鹕作品封面`,
  },
  tester: {
    title: (author: string, units: string): string => `${author} 的${units} | SandBase Lab`,
    backTesters: '← 测试者',
    since: (day: string): string => `测试者 · 自 ${day}`,
    everythingRan: (author: string): string => `${author} 运行过的一切`,
    everythingLede: '每个模型、每次运行，最佳优先。',
    modelsEyebrow: '模型',
    modelsTested: '测试过的模型',
    modelsLede: '点击某个模型与其他测试者对比。',
  },
  profile: {
    rank: '排名',
    overall: '总分',
    actuallyMoves: '真的会动',
    avgCost: '平均成本',
    runs: '运行次数',
    lastRun: '最近运行',
    testerCol: '测试者',
    modelCol: '模型',
    noPublicYet: (units: string): string => `暂无公开的${units}。`,
  },
  browse: {
    typeAria: '类型',
    all: '全部',
    classic: '经典',
    animated: '动画',
    sort: '排序',
    bestFirst: '最佳优先',
    wildestFirst: '最离谱优先',
    newest: '最新',
    cheapest: '最便宜',
    loadingUnits: (units: string): string => `正在加载${units}…`,
    count: (total: number, units: string): string => `${total} ${units}`,
    couldNotLoad: (units: string): string => `无法加载${units}。`,
    noneHere: (units: string): string => `这里还没有${units}。`,
    showMore: (units: string): string => `显示更多${units}`,
    byAria: '浏览方式',
    byModel: (n: number): string => `按模型 · ${n}`,
    byTester: (n: number): string => `按测试者 · ${n}`,
    tabByModel: '按模型',
    tabByTester: '按测试者',
    tabEverything: '全部',
    allTypes: '所有类型',
    models: '模型',
    testers: '测试者',
    loadingPelicans: '正在加载鹈鹕…',
    pelicanCount: (total: number): string => `${total} 只鹈鹕`,
    couldNotLoadPelicans: '无法加载鹈鹕。',
    noPelicans: '这里还没有鹈鹕。运行测试，你的将是第一只。',
    showMorePelicans: '显示更多鹈鹕',
  },
  myRuns: {
    title: '我的运行 | SandBase Lab',
    heading: '我的运行',
    lede: (name: string): string => `你用这个 SandBase 账户发起的每一次 ${name} 运行。`,
    signInTitle: '登录以查看你的运行。',
    signIn: '登录',
    loadError: '无法加载你的运行。',
    loading: '正在加载你的运行…',
    noneTitle: '还没有运行。',
    noneLede: (units: string): string => `挑几个模型，运行你的第一批${units}。`,
    status: { running: '运行中', succeeded: '已完成', failed: '未完成' },
    best: (model: string, score: string): string => `最佳 ${model} ${score}`,
    byline: (dateTime: string, models: number, author: string): string => `${dateTime} · ${models} 个模型 · 由 ${author}`,
  },
  media: {
    pelicanTitle: '骑自行车的鹈鹕',
    noPelican: '没有鹈鹕',
    noUnit: (unit: string): string => `没有${unit}`,
    noToy: '没有玩具',
    animation: '动画',
    playable: (unit: string): string => `可玩${unit}`,
    frameCaptions: ['初始画面', '20 根皮筋后', '炸开'],
    frameFallback: (n: number): string => `第 ${n} 帧`,
    screenshotsAria: '来自自动化浏览器的截图',
    clickToPlay: '点击开始',
    clickToPlayAria: (label: string, unit: string, score: number | null): string => `点击开始体验 ${label} 的${unit}${score != null ? `，最高分 ${score.toFixed(1)}/5` : ''}`,
    topScore: (score: string): string => `最高分 · ${score}`,
    topPick: '首选',
    by: (author: string): string => `由 ${author}`,
    perUnit: (unit: string): string => `每个${unit}`,
    cardAria: (model: string, kind: string, score: number | null): string => `${model}，${kind}${score != null ? `，得分 ${score.toFixed(1)}/5` : ''}`,
    openDetails: (label: string): string => `${label}。查看详情`,
    nothingYet: '这里还什么都没有。',
    noUnitsYet: (units: string): string => `还没有${units}。第一次运行会显示在这里。`,
    leaderboardAria: (name: string): string => `${name} 榜单`,
    rank: '排名',
    model: '模型',
    overall: '总分',
    actuallyMoves: '真的会动',
    avgCost: '平均成本',
    newPill: '新',
  },
  games: {
    guessLocked: '当四个模型都画过鹈鹕后，游戏即可解锁。',
    verdicts: [
      '是不是觉得鹈鹕都长一个样？',
      '鹈鹕新手。',
      '渐入佳境。',
      '眼光犀利。',
      '模型通。',
      '完美。你或许是个评审模型。',
    ],
    yourScore: '你的成绩',
    shareGuess: (score: number, total: number): string => `我在 SandBase Pelican Test 上猜对了 ${score}/${total} 只 AI 鹈鹕。你能赢我吗？`,
    playAgain: '再玩一次',
    round: (i: number, total: number, score: number): string => `第 ${i} / ${total} 轮 · 得分 ${score}`,
    whichModel: '这是哪个模型画的？',
    pickModel: '选一个模型',
    correct: '正确',
    nope: '错了',
    nice: '不错。',
    itWas: (model: string): string => `是 ${model}。`,
    judgeQuote: (comment: string): string => `评审：“${comment}”`,
    seeScore: '查看我的成绩',
    nextPelican: '下一只鹈鹕',
    faceOffLocked: '当两个模型上榜后，对决即可解锁。',
    leftCorner: '左方',
    rightCorner: '右方',
    openSide: (model: string, kind: string): string => `查看 ${model} 的${kind}鹈鹕`,
    noKindYet: (animated: boolean): string => `暂无${animated ? '动画' : '经典'}鹈鹕`,
    draws: '绘出率',
    winner: '胜者',
    beats: (winner: string, loser: string): string => `${modelLabel(winner)} 在 Pelican Test 上击败了 ${modelLabel(loser)}。`,
    versus: (a: string, b: string): string => `${modelLabel(a)} 对决 ${modelLabel(b)}，Pelican Test 上谁画得更好？`,
  },
  runPanel: {
    comingSoon: '敬请期待',
    comingSoonTitle: (scene: SceneSlug): string => `自行运行 ${brand(scene)} 即将推出。`,
    comingSoonBody: '目前由 SandBase Lab 官方运行；结果见上方。',
    signInTitle: (name: string): string => `登录以运行你自己的 ${name}。`,
    signInBody: (max: number, remixable: boolean, unit: string, units: string): string =>
      `使用你的 SandBase 账户。最多选择 ${max} 个模型${remixable ? `，保留经典${unit}或改编主题` : ''}。你只需为模型实际消耗的 token 付费，你的${units}也会加入展示墙。`,
    signInCta: '登录以运行',
    modelsLabel: (picked: number, max: number): string => `模型 · ${picked}/${max}`,
    clear: '清空',
    searchModels: '搜索模型',
    remove: (model: string): string => `移除 ${model}`,
    availableModels: '可选模型',
    loadingModels: '正在加载模型…',
    modelsUnavailable: '模型暂时不可用。',
    tests: '测试',
    subjectLabel: '主题（英文）',
    subjectKeep: (unit: string): string => `保留经典${unit}才能登上榜单。`,
    subjectRemix: '改编作品会出现在浏览区，但不计入榜单。',
    subjectInvalid: '请使用纯英文，3–120 个字符。',
    nameLabel: '显示名称（英文，可选）',
    namePlaceholder: 'Anonymous builder',
    nameInvalid: '2–32 个字符：字母、数字、空格、点、短横线或下划线。',
    worstCase: (count: number, units: string): string => `最坏情况（${count} 个${units} + 评分）`,
    billing: (max: string, daily: number): string => `按模型正常价格、依实际用量从你的 SandBase 账户计费，通常远低于此。每次运行上限 ${max}，每天 ${daily} 次。完成前请保持页面打开。`,
    topUp: '充值',
    signInAgain: '重新登录',
    starting: '启动中…',
    runScene: (scene: SceneSlug): string => `运行 ${brand(scene)}`,
    couldNotStart: '无法启动运行。',
  },
  compare: {
    rendering: '正在渲染你的对比短片…',
    ready: '你的对比视频已就绪。',
    videoError: '无法生成视频。',
    posterDownloaded: '对比海报已下载。',
    posterError: '无法生成海报。',
    eyebrow: '对比视频',
    title: '把这场对决做成短片。',
    scenes: (n: number): string => `${n} 个场景 · 1280×720 · 约 ${n * 2.8} 秒 · 在你的浏览器本地生成。`,
    needPelican: '这些模型至少各需要一只完成的鹈鹕，才能制作短片。',
    creating: '正在生成视频…',
    createVideo: '生成对比视频',
    downloadPoster: '下载海报',
    generatedAria: '生成的对比视频',
    downloadWebm: '下载 WebM',
    noWebm: '此浏览器无法录制 WebM，但仍可下载 PNG 海报。',
  },
  art: {
    pelicanAria: '插画：一只骑自行车的鹈鹕',
  },
  nav: {
    backTo: (name: string): string => `返回 ${name}`,
    pageNotFound: '找不到页面',
    pageNotFoundMsg: '该 Lab 页面不存在。',
    notFoundTitle: '找不到页面 | SandBase Lab',
  },
}

export const DICT = { en, 'zh-CN': zhCN }

/** The dictionary for the shared language. Falls back to English defensively. */
export function useT(): Dict {
  const { lang } = useLang()
  return DICT[lang] ?? DICT.en
}
